import React from 'react';

import { MIN_PASSWORD_LENGTH, ROUTES } from '@elara/client-core/constants';
import { CURRENT_PRIVACY_VERSION, CURRENT_TERMS_VERSION, PRIVACY_URL, TERMS_URL } from '@elara/domain/policy-versions';

import { useAuthFormState, useInviteState, useOnboardingState, useQueryParams } from '#states/hooks.js';
import { Alert, AppleIcon, Button, FloatingInput, GoogleIcon } from '#ui/index.js';
import { SessionLayout } from '#views/layout/SessionLayout.jsx';
import { Link } from '#views/Router.jsx';

export function SignupPage() {
  const authFormState = useAuthFormState();
  const inviteState = useInviteState();
  const onboardingState = useOnboardingState();
  const query = useQueryParams();

  const inviteParam = query.invite;
  const [hasTriedAutoValidate, setHasTriedAutoValidate] = React.useState(false);

  const isInviteValid = inviteState.hasValidInviteSession;
  const isInviteValidating = inviteState.isValidating;
  const inviteError = inviteState.error;

  // A code carried on the URL always wins over one remembered from an earlier visit. Invite links
  // are how the team hands out replacement codes, and a stale stored code used to make the link a
  // no-op: the page saw an existing "valid" session and never looked at the URL.
  React.useEffect(() => {
    if (!inviteParam || hasTriedAutoValidate) return;
    setHasTriedAutoValidate(true);
    void inviteState.validateInviteCode(inviteParam);
  }, [inviteParam, hasTriedAutoValidate, inviteState]);

  React.useEffect(() => {
    if (isInviteValid) authFormState.clearError();
  }, [isInviteValid, authFormState]);

  // Read signals directly in render for automatic reactivity
  const email = authFormState.email;
  const password = authFormState.password;
  const displayName = authFormState.displayName;
  const isLoading = authFormState.isLoading;
  const error = authFormState.error;

  // One banner for both failures. They are sequential steps of one submit, so only one can be
  // outstanding, and the member does not care which half of it failed — the auth error is the more
  // actionable of the two when both somehow linger, so it wins.
  const formError = error ?? onboardingState.profileCreationError;
  const isSubmitting = isLoading || onboardingState.isCreatingProfile;

  const recordAcceptance = () => {
    inviteState.setPendingAcceptance(CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION);
  };

  /**
   * The whole signup sequence: account, then profile, then straight to the payment step.
   *
   * The profile is created here rather than on the onboarding page because the payment step needs
   * the Stripe customer that profile creation mints — and because it puts the failure where the
   * member can act on it, still on the form they just submitted, with the invite code and policy
   * acceptance in hand. Onboarding keeps its own create as the recovery path for the case this one
   * does not land; `OnboardingState.createProfile` shares one in-flight promise, so the two cannot
   * race to consume the single-use invite code.
   *
   * `beginSignupSequence()` comes first, before the auth call, because Firebase flips the auth state
   * partway through this function and `App` would otherwise redirect off this page the moment the
   * account exists — landing the member on home and then a frame of onboarding, since the profile
   * has not arrived yet. The flag holds that redirect until the profile does arrive.
   *
   * Note what this deliberately does NOT do: navigate. Where to go next depends on whether this
   * membership owes a payment, and that answer lives on the profile — so the honest thing is to
   * wait for it rather than guess. Holding until the profile lands and letting `App`'s gates decide
   * sends a paying member to the payment step and a comped one straight to onboarding, each in one
   * hop. An unconditional navigate to the payment step got the paid case right and made the comped
   * case flicker through a screen it never needed.
   */
  const runSignupSequence = async (authenticate: () => Promise<boolean>) => {
    recordAcceptance();
    authFormState.beginSignupSequence();
    if (!(await authenticate())) return;
    await onboardingState.createProfile();
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSignupSequence(async () => authFormState.signUpWithEmail());
  };

  const handleGoogleSignUp = async () => runSignupSequence(async () => authFormState.signInWithGoogle());

  const handleAppleSignUp = async () => runSignupSequence(async () => authFormState.signInWithApple());

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await inviteState.validateInviteCode();
  };

  const footerContent = (
    <p>
      Already have an account?{' '}
      <Link to={ROUTES.LOGIN} className="text-accent hover:underline bg-transparent border-none p-0 m-0 cursor-pointer">
        Log in
      </Link>
    </p>
  );

  const showSignupForm = isInviteValid && !isInviteValidating;

  return (
    <SessionLayout title="Create your account" footer={footerContent}>
      {!showSignupForm && (
        <form onSubmit={handleInviteSubmit} className="space-y-6">
          {inviteError && <Alert variant="destructive">{inviteError}</Alert>}
          <div className="space-y-1">
            <FloatingInput
              id="invite-code"
              label="Invite code"
              type="text"
              value={inviteState.inviteCodeInput}
              onChange={(e) => inviteState.setInviteCodeInput(e.target.value)}
              disabled={isInviteValidating}
            />
            <div className="text-xs text-muted-foreground mt-2">
              Don’t have an invite?{' '}
              <a
                href="https://elara.travel/apply"
                className="text-accent hover:underline bg-transparent border-none p-0 m-0 cursor-pointer"
              >
                Join the waitlist
              </a>
            </div>
          </div>
          <div className="flex flex-col">
            <Button type="submit" size="lg" disabled={isInviteValidating}>
              {isInviteValidating ? 'Checking invite…' : 'Continue'}
            </Button>
          </div>
        </form>
      )}

      {showSignupForm && (
        <>
          <form onSubmit={handleEmailSignUp} className="space-y-6">
            {formError && (
              <Alert variant="destructive">
                <span className="whitespace-pre-line">{formError}</span>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-4">
              <FloatingInput
                id="signup-first-name"
                label="First name"
                type="text"
                value={authFormState.firstName}
                onChange={(e) => authFormState.setFirstName(e.target.value)}
                required
                disabled={isSubmitting}
              />
              <FloatingInput
                id="signup-last-name"
                label="Last name"
                type="text"
                value={authFormState.lastName}
                onChange={(e) => authFormState.setLastName(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <FloatingInput
              id="signup-email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => authFormState.setEmail(e.target.value)}
              required
              disabled={isSubmitting}
            />
            <div className="space-y-1">
              <FloatingInput
                id="signup-password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => authFormState.setPassword(e.target.value)}
                required
                disabled={isSubmitting}
                minLength={MIN_PASSWORD_LENGTH}
              />
              <p className="text-xs text-muted-foreground mt-2">At least {MIN_PASSWORD_LENGTH} characters.</p>
            </div>
            <div className="flex flex-col">
              <Button type="submit" size="lg" disabled={isSubmitting} className="w-full">
                {isSubmitting ? 'Setting up your account…' : 'Continue'}
              </Button>
            </div>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-muted" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-background-secondary px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <Button
              variant="icon-outline"
              size="icon-lg"
              onClick={handleGoogleSignUp}
              disabled={isSubmitting}
              className="rounded"
              tooltip="Sign up with Google"
              aria-label="Sign up with Google"
            >
              <GoogleIcon className="h-4 w-4" />
            </Button>
            {authFormState.isAppleSignInAvailable && (
              <Button
                variant="icon-outline"
                size="icon-lg"
                onClick={handleAppleSignUp}
                disabled={isSubmitting}
                className="rounded"
                tooltip="Sign up with Apple"
                aria-label="Sign up with Apple"
              >
                <AppleIcon className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/*
            Consent is stated rather than ticked. The checkbox this replaces was routinely missed —
            most of all on the social buttons, where the instinct is to go straight for Google or
            Apple — and the blocked attempt only explained itself after the fact. It sits below every
            signup path because it covers all of them: email, Google, and Apple each record the same
            acceptance on submit.
          */}
          <p className="mt-6 text-center text-xs text-muted-foreground">
            By continuing you accept our{' '}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
              Privacy Policy
            </a>
            .
          </p>
        </>
      )}
    </SessionLayout>
  );
}
